/**
 * BulletsDiff Component
 * 
 * Zeigt hinzugefügte und entfernte Bullet Points.
 * Keine JSON-Stringify, sondern visuelle Liste.
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

interface BulletsDiffProps {
  oldBullets: string[]
  newBullets: string[]
  oldLabel?: string
  newLabel?: string
}

interface DiffResult {
  added: string[]
  removed: string[]
  unchanged: string[]
  reordered: boolean
}

/**
 * Calculate diff between two bullet arrays
 */
function calculateBulletsDiff(
  oldBullets: string[],
  newBullets: string[]
): DiffResult {
  const oldSet = new Set(oldBullets)
  const newSet = new Set(newBullets)

  const added = newBullets.filter(b => !oldSet.has(b))
  const removed = oldBullets.filter(b => !newSet.has(b))
  const unchanged = oldBullets.filter(b => newSet.has(b))

  // Check for reordering (same items but different order)
  const sameItems = added.length === 0 && removed.length === 0
  const sameOrder = oldBullets.every((b, i) => newBullets[i] === b)
  const reordered = sameItems && !sameOrder && oldBullets.length > 0

  return { added, removed, unchanged, reordered }
}

export function BulletsDiff({
  oldBullets,
  newBullets,
  oldLabel = 'Alt',
  newLabel = 'Neu',
}: BulletsDiffProps) {
  const { t } = useTranslation()
  const diff = useMemo(
    () => calculateBulletsDiff(oldBullets, newBullets),
    [oldBullets, newBullets]
  )

  const hasChanges = diff.added.length > 0 || diff.removed.length > 0 || diff.reordered

  if (!hasChanges) {
    return (
      <div className="alert alert-info">
        <i className="bi bi-info-circle me-2" />
        {t('content.bulletsDiff')}
      </div>
    )
  }

  return (
    <div className="bullets-diff-container">
      {/* Summary */}
      <div className="d-flex gap-3 mb-3 small">
        {diff.added.length > 0 && (
          <span className="text-success">
            <i className="bi bi-plus-circle me-1" />
            {t('content.bulletsAdded', { count: diff.added.length })}
          </span>
        )}
        {diff.removed.length > 0 && (
          <span className="text-danger">
            <i className="bi bi-dash-circle me-1" />
            {diff.removed.length} entfernt
          </span>
        )}
        {diff.reordered && (
          <span className="text-warning">
            <i className="bi bi-arrow-left-right me-1" />
            {t('content.bulletsOrderChanged')}
          </span>
        )}
      </div>

      {/* Side-by-Side View */}
      <div className="row">
        {/* Old Bullets */}
        <div className="col-6">
          <div className="bullets-diff-column border rounded p-3">
            <div className="small text-muted mb-2">{oldLabel}</div>
            {oldBullets.length === 0 ? (
              <span className="text-muted fst-italic">Keine Bullets</span>
            ) : (
              <ul className="list-unstyled mb-0">
                {oldBullets.map((bullet, index) => {
                  const isRemoved = !newBullets.includes(bullet)
                  return (
                    <li 
                      key={index}
                      className={`bullets-diff-item ${isRemoved ? 'removed' : ''}`}
                    >
                      {isRemoved && (
                        <i className="bi bi-dash-circle text-danger me-2" />
                      )}
                      <span className={isRemoved ? 'text-decoration-line-through text-danger' : ''}>
                        • {bullet}
                      </span>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </div>

        {/* New Bullets */}
        <div className="col-6">
          <div className="bullets-diff-column border rounded p-3">
            <div className="small text-muted mb-2">{newLabel}</div>
            {newBullets.length === 0 ? (
              <span className="text-muted fst-italic">Keine Bullets</span>
            ) : (
              <ul className="list-unstyled mb-0">
                {newBullets.map((bullet, index) => {
                  const isAdded = !oldBullets.includes(bullet)
                  return (
                    <li 
                      key={index}
                      className={`bullets-diff-item ${isAdded ? 'added' : ''}`}
                    >
                      {isAdded && (
                        <i className="bi bi-plus-circle text-success me-2" />
                      )}
                      <span className={isAdded ? 'text-success fw-bold' : ''}>
                        • {bullet}
                      </span>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Reorder Indicator */}
      {diff.reordered && (
        <div className="alert alert-warning mt-3 mb-0">
          <i className="bi bi-arrow-left-right me-2" />
          {t('content.bulletOrderChangedDesc')}
        </div>
      )}
    </div>
  )
}


